import { PDFDocument } from "pdf-lib";
import { ReimbursementV2Request } from "../services/reimbursement-v2-api";
import { InvoiceV2Request } from "../services/invoice-v2-api";
import { createReimbursementFormFirstPageFromTemplate } from "./reimbursementFormPdf";
import { createInvoiceFormFirstPageFromTemplate } from "./invoiceFormPdf";

interface UserInfo {
  firstName: string;
  middleName?: string;
  lastName: string;
  homeAddress?: string;
}

/**
 * Canvas-based fallback for problematic image files
 */
const imageToPdfWithCanvas = async (imageFile: File): Promise<Uint8Array> => {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    const img = new Image();

    img.onload = async () => {
      try {
        // Set canvas dimensions to match image
        canvas.width = img.width;
        canvas.height = img.height;

        // Draw image on canvas
        ctx?.drawImage(img, 0, 0);

        // Convert canvas to blob as JPEG (more compatible)
        canvas.toBlob(
          async (blob) => {
            if (!blob) {
              reject(new Error("Failed to convert image to blob"));
              return;
            }

            try {
              // Create PDF document
              const pdfDoc = await PDFDocument.create();

              // Convert blob to array buffer
              const arrayBuffer = await blob.arrayBuffer();
              const imageBytes = new Uint8Array(arrayBuffer);

              // Embed as JPEG (canvas.toBlob with 'image/jpeg' produces valid JPEG)
              const embeddedImage = await pdfDoc.embedJpg(imageBytes);

              // Get image dimensions
              const { width: imgWidth, height: imgHeight } = embeddedImage;

              // Calculate page dimensions (A4 size with margins)
              const pageWidth = 595.28; // A4 width in points
              const pageHeight = 841.89; // A4 height in points
              const margin = 50;
              const maxWidth = pageWidth - margin * 2;
              const maxHeight = pageHeight - margin * 2;

              // Calculate scaling to fit image within page bounds
              const scaleX = maxWidth / imgWidth;
              const scaleY = maxHeight / imgHeight;
              const scale = Math.min(scaleX, scaleY, 1); // Don't upscale

              const scaledWidth = imgWidth * scale;
              const scaledHeight = imgHeight * scale;

              // Center the image on the page
              const x = (pageWidth - scaledWidth) / 2;
              const y = (pageHeight - scaledHeight) / 2;

              // Add page and draw image
              const page = pdfDoc.addPage([pageWidth, pageHeight]);
              page.drawImage(embeddedImage, {
                x,
                y,
                width: scaledWidth,
                height: scaledHeight,
              });

              // Save PDF
              const pdfBytes = await pdfDoc.save();
              resolve(pdfBytes);
            } catch (pdfError) {
              reject(pdfError);
            }
          },
          "image/jpeg",
          0.9,
        );
      } catch (error) {
        reject(error);
      }
    };

    img.onerror = () => {
      reject(new Error("Failed to load image"));
    };

    // Load image
    const reader = new FileReader();
    reader.onload = (e) => {
      img.src = e.target?.result as string;
    };
    reader.readAsDataURL(imageFile);
  });
};

/**
 * Converts an image file to a PDF document
 */
export const imageToPdf = async (imageFile: File): Promise<Uint8Array> => {
  try {
    // Create PDF document
    const pdfDoc = await PDFDocument.create();

    // Get image data directly from file
    const arrayBuffer = await imageFile.arrayBuffer();
    const imageBytes = new Uint8Array(arrayBuffer);

    // Try to embed image with fallback for problematic files
    let embeddedImage;
    try {
      if (imageFile.type === "image/jpeg" || imageFile.type === "image/jpg") {
        embeddedImage = await pdfDoc.embedJpg(imageBytes);
      } else if (imageFile.type === "image/png") {
        embeddedImage = await pdfDoc.embedPng(imageBytes);
      } else {
        throw new Error(`Unsupported image type: ${imageFile.type}`);
      }
    } catch {
      // If direct embedding fails, try canvas-based conversion as fallback
      return await imageToPdfWithCanvas(imageFile);
    }

    // Get image dimensions
    const { width: imgWidth, height: imgHeight } = embeddedImage;

    // Calculate page dimensions (A4 size with margins)
    const pageWidth = 595.28; // A4 width in points
    const pageHeight = 841.89; // A4 height in points
    const margin = 50;
    const maxWidth = pageWidth - margin * 2;
    const maxHeight = pageHeight - margin * 2;

    // Calculate scaling to fit image within page bounds
    const scaleX = maxWidth / imgWidth;
    const scaleY = maxHeight / imgHeight;
    const scale = Math.min(scaleX, scaleY, 1); // Don't upscale

    const scaledWidth = imgWidth * scale;
    const scaledHeight = imgHeight * scale;

    // Center the image on the page
    const x = (pageWidth - scaledWidth) / 2;
    const y = (pageHeight - scaledHeight) / 2;

    // Add page and draw image
    const page = pdfDoc.addPage([pageWidth, pageHeight]);
    page.drawImage(embeddedImage, {
      x,
      y,
      width: scaledWidth,
      height: scaledHeight,
    });

    // Save PDF
    const pdfBytes = await pdfDoc.save();
    return pdfBytes;
  } catch (error) {
    throw new Error(
      `Failed to convert image to PDF: ${
        error instanceof Error ? error.message : "Unknown error"
      }`,
    );
  }
};

/**
 * Replaces the first page of an existing PDF with a new reimbursement form page
 * while keeping all other pages intact
 */
export const replaceFirstPageOfPdf = async (
  existingPdfBlob: Blob,
  reimbursementData: ReimbursementV2Request,
  userInfo: UserInfo,
): Promise<File> => {
  // Load the existing PDF
  const existingPdfBytes = await existingPdfBlob.arrayBuffer();
  const existingPdf = await PDFDocument.load(existingPdfBytes);

  // Check if the PDF has more than one page
  const pageCount = existingPdf.getPageCount();

  if (pageCount === 1) {
    // If only one page, just create a new PDF with the reimbursement form
    const newPdf = await PDFDocument.create();
    const firstPageDoc = await createReimbursementFormFirstPageFromTemplate(
      reimbursementData,
      userInfo,
    );
    const firstPages = await newPdf.copyPages(
      firstPageDoc,
      firstPageDoc.getPageIndices(),
    );
    firstPages.forEach((page) => newPdf.addPage(page));

    const newPdfBytes = await newPdf.save();
    return new File([new Uint8Array(newPdfBytes)], "merged.pdf", {
      type: "application/pdf",
    });
  }

  // If multi-page, replace the first page and keep the rest
  const newPdf = await PDFDocument.create();

  // Add the new first page
  const firstPageDoc = await createReimbursementFormFirstPageFromTemplate(
    reimbursementData,
    userInfo,
  );
  const firstPages = await newPdf.copyPages(
    firstPageDoc,
    firstPageDoc.getPageIndices(),
  );
  firstPages.forEach((page) => newPdf.addPage(page));

  // Copy all pages except the first one from the existing PDF
  const remainingPageIndices = Array.from(
    { length: pageCount - 1 },
    (_, i) => i + 1,
  );
  const remainingPages = await newPdf.copyPages(
    existingPdf,
    remainingPageIndices,
  );
  remainingPages.forEach((page) => newPdf.addPage(page));

  const newPdfBytes = await newPdf.save();
  return new File([new Uint8Array(newPdfBytes)], "merged.pdf", {
    type: "application/pdf",
  });
};

/**
 * Merges multiple files (PDFs and images) into a single PDF with a professional first page
 */
export const mergeFilesToPdf = async (
  files: File[],
  reimbursementData?: ReimbursementV2Request,
  userInfo?: UserInfo,
): Promise<File> => {
  // If no files provided, we must have reimbursement data to generate the form PDF
  if (files.length === 0 && (!reimbursementData || !userInfo)) {
    throw new Error("No files to merge and no reimbursement data provided");
  }

  const mergedPdf = await PDFDocument.create();

  // Add first page if reimbursement data and user info are provided
  if (reimbursementData && userInfo) {
    try {
      const firstPageDoc = await createReimbursementFormFirstPageFromTemplate(
        reimbursementData,
        userInfo,
      );

      // Get the pages from the first page document directly
      const firstPageIndices = firstPageDoc.getPageIndices();

      const firstPages = await mergedPdf.copyPages(
        firstPageDoc,
        firstPageIndices,
      );
      firstPages.forEach((page) => mergedPdf.addPage(page));
    } catch (error) {
      console.error("Failed to create first page:", error);
      throw new Error("Failed to create reimbursement form PDF");
    }
  }

  // Merge user-provided files if any
  for (const file of files) {
    try {
      if (file.type === "application/pdf") {
        // Handle PDF files
        const arrayBuffer = await file.arrayBuffer();
        const pdf = await PDFDocument.load(arrayBuffer);
        const pages = await mergedPdf.copyPages(pdf, pdf.getPageIndices());
        pages.forEach((page) => mergedPdf.addPage(page));
      } else if (file.type.startsWith("image/")) {
        // Handle image files
        const pdfBytes = await imageToPdf(file);
        const pdf = await PDFDocument.load(pdfBytes);
        const pages = await mergedPdf.copyPages(pdf, pdf.getPageIndices());
        pages.forEach((page) => mergedPdf.addPage(page));
      }
    } catch (_) {
      throw new Error(`Failed to process file: ${file.name}`);
    }
  }

  // Generate merged PDF bytes
  const mergedPdfBytes = await mergedPdf.save();

  // Create a new File object with the merged PDF
  const mergedFile = new File([new Uint8Array(mergedPdfBytes)], "merged.pdf", {
    type: "application/pdf",
  });

  return mergedFile;
};

/**
 * Generates a filename for the merged PDF based on reimbursement data
 */
export const generateMergedPdfFileName = ({
  date,
  category,
  vendor,
  item,
  price,
  username,
}: {
  date?: string;
  category?: number;
  vendor?: string;
  item?: string;
  price?: number;
  username?: string;
}): string => {
  const today = new Date();
  const dateStr = date
    ? new Date(date).toISOString().slice(0, 10).replace(/-/g, "")
    : today.toISOString().slice(0, 10).replace(/-/g, "");

  const categoryNum = category ? category : 0;
  const userName = username?.replace(/[\s.-]/g, "_") ?? "User";
  const vendorText = vendor ? vendor.replace(/[\s.\-&]/g, "_") : "NoInfo";
  const itemText = item ? item.replace(/[\s.\-&]/g, "_") : "NoInfo";
  const priceNum = price ? price : 0;

  return `${dateStr}_${categoryNum}_${userName}_${vendorText}_${itemText}_${priceNum}_merged.pdf`;
};

/**
 * Merges multiple files (PDFs and images) into a single PDF with a professional first page for invoices
 */
export const mergeFilesToPdfForInvoice = async (
  files: File[],
  invoiceData?: InvoiceV2Request,
  userInfo?: UserInfo,
): Promise<File> => {
  if (files.length === 0) {
    throw new Error("No files to merge");
  }

  const mergedPdf = await PDFDocument.create();

  // Add first page if invoice data and user info are provided
  if (invoiceData && userInfo) {
    try {
      const firstPageDoc = await createInvoiceFormFirstPageFromTemplate(
        invoiceData,
        userInfo,
      );

      // Get the pages from the first page document directly
      const firstPageIndices = firstPageDoc.getPageIndices();

      const firstPages = await mergedPdf.copyPages(
        firstPageDoc,
        firstPageIndices,
      );

      firstPages.forEach((page) => {
        mergedPdf.addPage(page);
      });
    } catch (error) {
      console.error("Failed to create first page:", error);
      // Continue without first page if it fails
    }
  }

  // Process and add remaining files
  for (const file of files) {
    try {
      if (file.type === "application/pdf") {
        const arrayBuffer = await file.arrayBuffer();
        const pdfBytes = new Uint8Array(arrayBuffer);
        const pdf = await PDFDocument.load(pdfBytes);
        const pages = await mergedPdf.copyPages(pdf, pdf.getPageIndices());
        pages.forEach((page) => {
          mergedPdf.addPage(page);
        });
      } else if (file.type.startsWith("image/")) {
        const pdfBytes = await imageToPdfWithCanvas(file);
        const imagePdf = await PDFDocument.load(pdfBytes);
        const pages = await mergedPdf.copyPages(
          imagePdf,
          imagePdf.getPageIndices(),
        );
        pages.forEach((page) => {
          mergedPdf.addPage(page);
        });
      }
    } catch (error) {
      console.error(`Failed to process file ${file.name}:`, error);
      // Continue with next file if one fails
    }
  }

  // Generate merged PDF bytes
  const mergedPdfBytes = await mergedPdf.save();

  // Create a new File object with the merged PDF
  const mergedFile = new File([new Uint8Array(mergedPdfBytes)], "merged.pdf", {
    type: "application/pdf",
  });

  return mergedFile;
};

/**
 * Generates a filename for the merged invoice PDF
 */
export const generateMergedInvoicePdfFileName = ({
  date,
  totalAmount,
  username,
}: {
  date?: string;
  totalAmount?: number;
  username?: string;
}): string => {
  const today = new Date();
  const dateStr = date
    ? new Date(date).toISOString().slice(0, 10).replace(/-/g, "")
    : today.toISOString().slice(0, 10).replace(/-/g, "");

  const userName = username?.replace(/[\s.-]/g, "_") ?? "User";
  const amount = totalAmount ? totalAmount : 0;

  return `${dateStr}_${userName}_${amount}_invoice_merged.pdf`;
};
